import { useEffect, useMemo, type ReactElement, type ReactNode } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { UserRole } from '../auth/types';
import { useAuth } from '../auth/useAuth';
import { appEnv } from '../lib/env';
import { getPrimaryUserRole, getTokenUserRoles } from '../auth/roles';

interface KeycloakAuthenticatedProps {
  onAuthenticating: ReactNode;
  children: ReactNode;
}

interface SyncSessionParams {
  keycloak: ReturnType<typeof useKeycloak>['keycloak'];
  token?: string;
  userName?: string;
  userRole?: UserRole;
  userRoles: UserRole[];
  triggerInit: ReturnType<typeof useAuth>['triggerInit'];
  signout: ReturnType<typeof useAuth>['signout'];
}

function useSyncSessionWithKeycloak({
  keycloak,
  token,
  userName,
  userRole,
  userRoles,
  triggerInit,
  signout
}: SyncSessionParams): void {
  useEffect(() => {
    if (keycloak.token && userName && userRole && userRoles.length > 0) {
      if (token !== keycloak.token) {
        triggerInit({
          token: keycloak.token,
          userName,
          userRoles
        });
      }
    }

    if (keycloak.authenticated && token && (!userRole || userRoles.length === 0)) {
      signout();
    }

    if (!keycloak.authenticated && token) {
      signout();
    }
  }, [keycloak, token, userName, userRole, userRoles, triggerInit, signout]);
}

export function KeycloakAuthenticated({ onAuthenticating, children }: KeycloakAuthenticatedProps): ReactElement {
  const { keycloak, initialized } = useKeycloak();
  const { token, triggerInit, signout } = useAuth();

  const userRoles = useMemo(() => {
    return getTokenUserRoles(keycloak.tokenParsed, appEnv.irisClientId);
  }, [keycloak.tokenParsed]);

  const userRole = useMemo(() => {
    return getPrimaryUserRole(userRoles);
  }, [userRoles]);

  const userName = keycloak.tokenParsed?.preferred_username ?? keycloak.tokenParsed?.name ?? 'unknown-user';

  useSyncSessionWithKeycloak({
    keycloak,
    token,
    userName,
    userRole,
    userRoles,
    triggerInit,
    signout
  });

  if (!initialized || !keycloak.authenticated) {
    return <>{onAuthenticating}</>;
  }

  if (!userRole || userRoles.length === 0) {
    return <div style={{ padding: '2rem' }}>Your account does not have access to this application.</div>;
  }

  return <>{children}</>;
}
